import React, { useState, useContext } from 'react';
import {
    Box, Card, CardContent, Typography, TextField, Button, Grid, Avatar,
    Divider, Alert, Snackbar, IconButton, InputAdornment, Chip, Fade,
    CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import { AuthContext } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const ROLE_LABELS = {
    admin: 'Administrateur',
    director: 'Directeur RH',
    trainer: 'Formateur',
};

const ROLE_COLORS = {
    admin: '#1a237e',
    director: '#1a5276',
    trainer: '#1b5e20',
};

export default function AccountPage() {
    const { user, setUser } = useContext(AuthContext);

    // ── Profile form state ─────────────────────────────────
    const [profile, setProfile] = useState({
        nom: user?.nom || '',
        prenom: user?.prenom || '',
        email: user?.email || '',
        phone: user?.phone || '',
        cin: user?.cin || '',
        diplomes: user?.diplomes || '',
        profession: user?.profession || '',
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileErrors, setProfileErrors] = useState({});

    // ── Password form state ────────────────────────────────
    const [passwords, setPasswords] = useState({
        current_password: '',
        password: '',
        password_confirmation: '',
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({});
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // ── Snackbar state ─────────────────────────────────────
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSuccess = (message) => setSnackbar({ open: true, message, severity: 'success' });
    const showError = (message) => setSnackbar({ open: true, message, severity: 'error' });

    // ── Handle profile save ────────────────────────────────
    const handleProfileSave = async (e) => {
        e.preventDefault();
        setProfileLoading(true);
        setProfileErrors({});

        try {
            const { data } = await authAPI.updateProfile(profile);
            // Update context + localStorage
            const updatedUser = data.user;
            if (setUser) {
                setUser(updatedUser);
            }
            localStorage.setItem('ista_user', JSON.stringify(updatedUser));
            showSuccess(data.message || 'Profil mis à jour avec succès');
        } catch (err) {
            const resp = err.response?.data;
            if (resp?.errors) {
                setProfileErrors(resp.errors);
            }
            showError(resp?.message || 'Erreur lors de la mise à jour du profil');
        } finally {
            setProfileLoading(false);
        }
    };

    // ── Handle password change ─────────────────────────────
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordErrors({});

        try {
            const { data } = await authAPI.changePassword(passwords);
            setPasswords({ current_password: '', password: '', password_confirmation: '' });
            showSuccess(data.message || 'Mot de passe modifié avec succès');
        } catch (err) {
            const resp = err.response?.data;
            if (resp?.errors) {
                setPasswordErrors(resp.errors);
            }
            showError(resp?.message || 'Erreur lors du changement de mot de passe');
        } finally {
            setPasswordLoading(false);
        }
    };

    const initials = `${user?.prenom?.[0] || ''}${user?.nom?.[0] || ''}`.toUpperCase();
    const roleColor = ROLE_COLORS[user?.role] || '#333';

    return (
        <Fade in timeout={400}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* ── Header Card ─────────────────────────────────── */}
                <Card sx={{
                    mb: 3,
                    background: `linear-gradient(135deg, ${roleColor} 0%, ${roleColor}dd 50%, ${roleColor}99 100%)`,
                    color: 'white',
                    borderRadius: 3,
                    overflow: 'visible',
                    position: 'relative',
                }}>
                    <CardContent sx={{ py: 4, px: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Avatar sx={{
                            width: 80, height: 80,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            fontSize: '1.8rem',
                            fontWeight: 700,
                            border: '3px solid rgba(255,255,255,0.4)',
                            backdropFilter: 'blur(10px)',
                        }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {user?.prenom} {user?.nom}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                                {user?.email}
                            </Typography>
                            <Chip
                                label={ROLE_LABELS[user?.role] || user?.role}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 600,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            />
                        </Box>
                        <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                Membre depuis
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                }) : '—'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    {/* ── Profile Information Card ─────────────────── */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: 3, height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 2,
                                        bgcolor: `${roleColor}15`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <PersonIcon sx={{ color: roleColor }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                        Informations personnelles
                                    </Typography>
                                </Box>

                                <form onSubmit={handleProfileSave}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                id="profile-prenom"
                                                label="Prénom"
                                                value={profile.prenom}
                                                onChange={(e) => setProfile({ ...profile, prenom: e.target.value })}
                                                fullWidth required
                                                error={!!profileErrors.prenom}
                                                helperText={profileErrors.prenom?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                id="profile-nom"
                                                label="Nom"
                                                value={profile.nom}
                                                onChange={(e) => setProfile({ ...profile, nom: e.target.value })}
                                                fullWidth required
                                                error={!!profileErrors.nom}
                                                helperText={profileErrors.nom?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                id="profile-email"
                                                label="Adresse email"
                                                type="email"
                                                value={profile.email}
                                                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                                fullWidth required
                                                error={!!profileErrors.email}
                                                helperText={profileErrors.email?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <EmailIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                id="profile-phone"
                                                label="Téléphone"
                                                value={profile.phone}
                                                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                                fullWidth
                                                error={!!profileErrors.phone}
                                                helperText={profileErrors.phone?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                id="profile-cin"
                                                label="CIN"
                                                value={profile.cin}
                                                onChange={(e) => setProfile({ ...profile, cin: e.target.value })}
                                                fullWidth
                                                error={!!profileErrors.cin}
                                                helperText={profileErrors.cin?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <BadgeIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                id="profile-profession"
                                                label="Profession"
                                                value={profile.profession}
                                                onChange={(e) => setProfile({ ...profile, profession: e.target.value })}
                                                fullWidth
                                                error={!!profileErrors.profession}
                                                helperText={profileErrors.profession?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <WorkIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                id="profile-diplomes"
                                                label="Diplômes"
                                                value={profile.diplomes}
                                                onChange={(e) => setProfile({ ...profile, diplomes: e.target.value })}
                                                fullWidth multiline rows={2}
                                                error={!!profileErrors.diplomes}
                                                helperText={profileErrors.diplomes?.[0]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                                            <SchoolIcon sx={{ color: '#999', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                        <Button
                                            id="save-profile-btn"
                                            type="submit"
                                            variant="contained"
                                            disabled={profileLoading}
                                            startIcon={profileLoading ? <CircularProgress size={18} /> : <SaveIcon />}
                                            sx={{
                                                px: 4, py: 1.2,
                                                bgcolor: roleColor,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                boxShadow: `0 4px 14px ${roleColor}40`,
                                                '&:hover': {
                                                    bgcolor: roleColor,
                                                    boxShadow: `0 6px 20px ${roleColor}60`,
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {profileLoading ? 'Enregistrement...' : 'Enregistrer'}
                                        </Button>
                                    </Box>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Password Change Card ────────────────────── */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ borderRadius: 3, height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 2,
                                        bgcolor: '#f44336' + '15',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <LockIcon sx={{ color: '#c62828' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                        Sécurité
                                    </Typography>
                                </Box>

                                <form onSubmit={handlePasswordChange}>
                                    <TextField
                                        id="current-password"
                                        label="Mot de passe actuel"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        value={passwords.current_password}
                                        onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                                        fullWidth required
                                        error={!!passwordErrors.current_password}
                                        helperText={passwordErrors.current_password?.[0]}
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                                                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Divider sx={{ my: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Nouveau mot de passe
                                        </Typography>
                                    </Divider>

                                    <TextField
                                        id="new-password"
                                        label="Nouveau mot de passe"
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={passwords.password}
                                        onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                                        fullWidth required
                                        error={!!passwordErrors.password}
                                        helperText={passwordErrors.password?.[0] || 'Minimum 8 caractères'}
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        id="confirm-password"
                                        label="Confirmer le nouveau mot de passe"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={passwords.password_confirmation}
                                        onChange={(e) => setPasswords({ ...passwords, password_confirmation: e.target.value })}
                                        fullWidth required
                                        error={!!passwordErrors.password_confirmation}
                                        helperText={passwordErrors.password_confirmation?.[0]}
                                        sx={{ mb: 3 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Button
                                        id="change-password-btn"
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        disabled={passwordLoading}
                                        startIcon={passwordLoading ? <CircularProgress size={18} /> : <LockIcon />}
                                        sx={{
                                            py: 1.2,
                                            bgcolor: '#c62828',
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            boxShadow: '0 4px 14px rgba(198,40,40,0.3)',
                                            '&:hover': {
                                                bgcolor: '#b71c1c',
                                                boxShadow: '0 6px 20px rgba(198,40,40,0.4)',
                                                transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {passwordLoading ? 'Modification...' : 'Modifier le mot de passe'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Success / Error Snackbar ────────────────────── */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        variant="filled"
                        icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : undefined}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Fade>
    );
}
